import { useCallback, useEffect, useMemo, useRef } from "react";
import stringify from "fast-json-stable-stringify";

import { TeamMembership } from "@/domain/team/models/team-membership-model";
import { TeamMembershipRole, TeamMembershipStatus } from "@/domain/team/models/team-model";
import {
  WorkspaceMembership,
  WorkspaceMembershipRole,
  WorkspaceMembershipStatus,
} from "@/domain/workspace/models/workspace-membership-model";
import type { UserListForWorkspaceQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { userListForWorkspaceQueryDocument } from "./user.gql";

export interface UseGetUserProps {
  workspaceId: string;
  enabled?: boolean;
}

export type UserListFromWorkspace = UserListForWorkspaceQuery["userListForWorkspace"];
export type UserFromWorkspaceList = UserListFromWorkspace[number];

type User = {
  teamMembershipList: TeamMembership[];
  workspaceMembershipList: WorkspaceMembership[];
};

const matchesWorkspaceFilters = (
  user: User,
  filters?: {
    role?: WorkspaceMembershipRole[];
    status?: WorkspaceMembershipStatus[];
  },
) => {
  if (!filters) return true;

  const roleMatches =
    !filters.role ||
    user.workspaceMembershipList?.some((membership) =>
      filters.role?.includes(membership.role as WorkspaceMembershipRole),
    );
  const statusMatches =
    !filters.status ||
    user.workspaceMembershipList?.some((membership) =>
      filters.status?.includes(membership.status as WorkspaceMembershipStatus),
    );

  return roleMatches && statusMatches;
};

const matchesTeamFilters = (
  user: User,
  filters?: {
    teamIdList: string[];
    role?: TeamMembershipRole[];
    status?: TeamMembershipStatus[];
  },
) => {
  if (!filters) return true;

  const relevantTeamMemberships = user.teamMembershipList?.filter((membership) =>
    filters.teamIdList?.includes(membership.teamId),
  );

  const teamIdMatches = relevantTeamMemberships.length > 0;
  const roleMatches =
    !filters.role ||
    relevantTeamMemberships?.some((membership) => filters.role?.includes(membership.role as TeamMembershipRole));
  const statusMatches =
    !filters.status ||
    relevantTeamMemberships?.some((membership) => filters.status?.includes(membership.status as TeamMembershipStatus));

  return teamIdMatches && roleMatches && statusMatches;
};

export const useGetUserListForWorkspace = ({ workspaceId, enabled = true }: UseGetUserProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.users.list(workspaceId),
    queryFn: async (): Promise<UserListForWorkspaceQuery> => {
      const client = createOperationClient("userListForWorkspace");
      return await client.request(userListForWorkspaceQueryDocument, {
        input: {
          workspaceId: workspaceId,
        },
      });
    },
    enabled: enabled,
  });

  const userList = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }
    return queryResult.data.userListForWorkspace;
  }, [queryResult.data]);

  useEffect(() => {
    cacheRef.current.clear();
  }, [queryResult.data]);

  const cacheRef = useRef(new Map<string, Array<(typeof userList)[0]>>());

  const filterUsers = useCallback(
    (filters?: {
      workspaceMembership?: {
        role?: WorkspaceMembershipRole[];
        status?: WorkspaceMembershipStatus[];
      };
      teamMembership?: {
        teamIdList: string[];
        role?: TeamMembershipRole[];
        status?: TeamMembershipStatus[];
      };
    }) => {
      if (!userList) {
        return [];
      }

      const cacheKey = stringify(filters);
      if (cacheRef.current.has(cacheKey)) {
        const chachedList = cacheRef.current.get(cacheKey);
        if (chachedList) {
          return chachedList;
        }
      }

      const filteredUsers = userList.filter((user) => {
        const workspaceMatches = matchesWorkspaceFilters(user, filters?.workspaceMembership);
        const teamMatches = matchesTeamFilters(user, filters?.teamMembership);

        return workspaceMatches && teamMatches;
      });

      cacheRef.current.set(cacheKey, filteredUsers);
      return filteredUsers;
    },
    [userList],
  );

  return {
    userList,
    queryResult,
    loading: queryResult.isLoading,
    error: queryResult.error,
    filterUsers,
  };
};
